// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ListingCategories from "@gcg/App/Components/ListingCategories/ListingCategories";
import PDFViewer from "@gcg/App/Components/PDFViewer/PDFViewer";
import ProductCarouselComponent from "@gcg/App/Components/ProductCarouselComponent/ProductCarouselComponent";
import loadKnowledgeCenterDetails from "@gcg/App/Handlers/LoadKnowledgeCenterDetails";
import { ProductCarouselProductStyles } from "@gcg/Overrides/Components/ProductCarouselProduct";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getBrandById } from "@insite/client-framework/Services/BrandService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setBreadcrumbs from "@insite/client-framework/Store/Components/Breadcrumbs/Handlers/SetBreadcrumbs";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button from "@insite/mobius/Button";
import Link from "@insite/mobius/Link/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    location: getLocation(state),
    url: state?.context?.website?.uri?.split("/")?.splice(0, 3)?.join("/"),
    categoriesDataView: getCategoriesDataView(state),
    contactUsLink: getPageLinkByPageType(state, "Contact-Us")?.url || "/ContactUs",
});

const mapDispatchToProps = {
    loadKnowledgeCenterDetails,
    loadCarouselProducts,
    loadProductInfoList,
    setBreadcrumbs,
    loadCategories,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface KnowledgeCenterStyles {
    wrapper?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    tagsDiv?: InjectableCss;
    typeWrapper?: InjectableCss;
    productCarouselStyles?: ProductCarouselProductStyles;
    relatedProductsWrapper?: InjectableCss;
}

export const styles: KnowledgeCenterStyles = {
    typeWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-top: 12px;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        color: "primary.main",
        css: css`
            margin: 0 auto;
        `,
    },
    tagsDiv: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            padding: 4px;
        `,
    },
    productCarouselStyles: {
        productImageWrapperStyles: {
            css: css`
                width: 100px;
                height: 100px;
                margin: auto;
            `,
        },
    },
    relatedProductsWrapper: {
        css: css`
            margin-top: 20px;
            // padding: 16px;
        `,
    },
};

const customFields = {
    showImage: true,
    maxNumberOfColumns: 6,
    carouselTemplat: "carousel",
    showPartNumbers: true,
    carouselType: "relatedProducts",
    showTitle: true,
    showBrand: true,
    showPrice: true,
    showAddToCart: false,
    showAddToList: false,
};

const KnowledgeCenter: React.FC<Props> = ({
    loadKnowledgeCenterDetails,
    loadProductInfoList,
    setBreadcrumbs,
    location,
    url,
    categoriesDataView,
    loadCategories,
    history,
    contactUsLink,
}) => {
    const [dataLoaded, setDataLoaded] = useState(false);
    const [loadedDocument, setLoadedDocument] = useState({} as any);
    const [products, setProducts] = useState([]);
    const [brand, setBrand] = useState({} as any);
    const [embedDocument, setEmbedDocument] = useState(false);

    const breadCrumbLinks = [
        {
            children: "Home",
            href: "/",
        },
        {
            children: "Knowledge Center",
            href: "/Knowledge-Center",
        },
    ];

    const loadData = () => {
        const urlParams = location.search.split("?id=")[1];
        const params: any = {
            id: urlParams,
        };
        if (params.id !== undefined && params.id !== "") {
            const canonical = (document as any)?.querySelector('link[rel="canonical"]');
            const url = canonical?.getAttribute("href");
            canonical?.setAttribute("href", url ? `${url}?id=${params.id}` : "");

            loadKnowledgeCenterDetails({
                params,
                onComplete: (response: any) => {
                    const apiResult = response.apiResult;
                    if (apiResult) {
                        const links = [...breadCrumbLinks, { children: apiResult.document.fileName, href: "" }];
                        setDataLoaded(true);
                        setBreadcrumbs({ links });
                        setLoadedDocument(apiResult.document);
                        setProducts(apiResult.relatedProduct);
                        setEmbedDocument(apiResult.embedDocument);
                        loadBrand({ brandId: apiResult.document.brandsId[0] });
                        const productIds = apiResult.relatedProduct.map((item: any) => item.id);
                        loadProductInfoList({
                            id: `product-carousel-knowledge-center`,
                            getProductCollectionParameter: { productIds },
                        });
                    }
                },
            });
        }
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories();
        }
    };
    React.useEffect(() => {
        loadData();
    }, [location]);

    const loadBrand = async (id: any) => {
        const apiBrand = await getBrandById(id);
        setBrand(apiBrand);
    };

    if (!dataLoaded) {
        return (
            <StyledWrapper
                css={css`
                    height: 300px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                `}
            >
                <LoadingSpinner color="primary.main" />
            </StyledWrapper>
        );
    }

    const jsonLd: any = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@context": "https://schema.org",
                "@type": "DigitalDocument",
                name: loadedDocument?.fileName || loadedDocument?.fileDescription,
                description: `${loadedDocument?.productCategory}, ${loadedDocument.fileDescription} ${loadedDocument.documentType}`,
                url: url + location.pathname + location.search,
            },
        ],
    };

    if (!categoriesDataView.value || !loadedDocument) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    const categories = categoriesDataView?.value.filter(
        category => loadedDocument?.categoriesId?.indexOf(category.id) > -1,
    );

    const goToContactUs = () => {
        history.push(contactUsLink);
    };

    return (
        <StyledWrapper>
            <Typography variant="h1">{loadedDocument?.fileName}</Typography>
            <Typography variant="h3">{loadedDocument?.fileDescription}</Typography>
            <Typography
                as="div"
                css={css`
                    display: flex;
                `}
            >
                <Typography
                    as="div"
                    css={css`
                        padding: 4px;
                        padding-left: 0;
                    `}
                >
                    {translate("Category: ")}
                </Typography>
                <Typography as="div" {...styles.tagsDiv}>
                    <ListingCategories
                        items={categories}
                        viewMoreButtonAsText={true}
                        categoriesNumber={5}
                        view="inline"
                    />
                </Typography>
            </Typography>
            {Object.keys(brand).length !== 0 && (
                <Typography as="p">
                    {translate("Brand: ")} <Link href={brand.detailPagePath}>{brand.name}</Link>
                </Typography>
            )}
            <StyledWrapper {...styles.typeWrapper}>
                {loadedDocument.documentType && (
                    <Typography as="p">
                        {translate("Type: ")} {loadedDocument?.documentType}
                    </Typography>
                )}
                <Button onClick={goToContactUs} variant="primary" id="requestInfo" typographyProps={{id: "requestInfo"} as any}>
                    {translate("Request Info")}
                </Button>
            </StyledWrapper>
            <PDFViewer
                filePath={loadedDocument?.fileUrl}
                fileName={loadedDocument?.fileName}
                dataTestSelector={`knowledge_center_${loadedDocument?.fileName}_${loadedDocument?.filePath}`}
                asLink={!embedDocument}
                newTab={true}
            />
            {products && products.length > 0 && (
                <StyledWrapper {...styles.relatedProductsWrapper}>
                    <Typography variant="h2">{translate("Related Products")}</Typography>
                    <ProductCarouselComponent
                        fields={customFields}
                        products={products}
                        id="product-carousel-knowledge-center"
                        extendedStyles={styles.productCarouselStyles}
                        hideAvailability={true}
                        showElipsesOn="1"
                        spaceBetween={15}
                    />
                </StyledWrapper>
            )}
            {/* eslint-disable-next-line react/no-danger */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
        </StyledWrapper>
    );
};
export const definition: WidgetDefinition = {
    group: "Basic",
    fieldDefinitions: [],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(KnowledgeCenter)),
    definition,
};

export default widgetModule;
